import { useRef } from "react";
import { useLenis } from "lenis/react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { BRAND, IMG } from "../lib/site";

const EASE = [0.16, 1, 0.3, 1];

function MaskWord({ children, initialClip, extraInitial = {}, delay, ready, className }) {
  return (
    <span className="block overflow-hidden pb-[0.06em]">
      <motion.span
        className={`block will-change-transform ${className}`}
        style={{ letterSpacing: "-0.055em" }}
        initial={{ clipPath: initialClip, ...extraInitial }}
        animate={ready ? { clipPath: "inset(0 0 0 0)", x: 0, y: 0 } : {}}
        transition={{ duration: 1.0, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

// Signature hero visual: pointing woman on the right.

export default function Hero({ ready }) {
  const ref = useRef(null);
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-28%"]);
  const wallY = useTransform(scrollYProgress, [0, 1], ["0%", "-12%"]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.85, 1], [1, 1, 0]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) (lenis ? lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" }));
  };

  const size = "text-[15.5vw] leading-[0.82] sm:text-[13.5vw] lg:text-[9vw] xl:text-[8.2vw]";

  return (
    <section
      ref={ref}
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-pp-black noise"
    >
      {/* Interior wall backdrop */}
      <motion.div className="absolute inset-0 z-0" style={{ y: bgY, scale: bgScale }}>
        <img src={IMG.heroInterior} alt="Framed photographs on a premium wall" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-pp-black/70" />
      </motion.div>

      <div className="relative z-10 mx-auto grid min-h-[100svh] max-w-[1500px] grid-cols-1 items-center gap-8 px-5 pb-16 pt-28 md:px-10 lg:grid-cols-12 lg:pt-24">
        {/* Text */}
        <motion.div style={{ y: textY, opacity: textOpacity }} className="relative z-20 lg:col-span-7">
          <motion.div
            className="mb-5 flex items-center gap-4"
            initial={{ opacity: 0 }}
            animate={ready ? { opacity: 1 } : {}}
            transition={{ duration: 0.7, delay: 0.05 }}
          >
            <span className="h-px w-10 bg-pp-red" />
            <span className="overline text-white">PHOTO&nbsp;PIXEL®</span>
          </motion.div>

          <h1 className="font-display font-black leading-[0.84] tracking-tight text-white" data-testid="hero-slogan">
            <MaskWord ready={ready} delay={0.15} initialClip="inset(0 100% 0 0)" extraInitial={{ x: "-6%" }} className={`text-white ${size}`}>MEMORIES</MaskWord>
            <MaskWord ready={ready} delay={0.36} initialClip="inset(100% 0 0 0)" extraInitial={{ y: "40%" }} className={`text-white ${size}`}>WORTH</MaskWord>
            <MaskWord ready={ready} delay={0.56} initialClip="inset(0 100% 0 0)" extraInitial={{ x: "8%" }} className={`text-pp-red ${size}`}>FRAMING.</MaskWord>
          </h1>

          <motion.p
            className="mt-7 max-w-xl text-sm leading-relaxed text-white/75 md:text-base"
            initial={{ opacity: 0, y: 16 }}
            animate={ready ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
          >
            Premium Photo Frames, Photo Printing, Personalized Photo Gifts &amp; Wall Art.
          </motion.p>

          <motion.div
            className="mt-8 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 16 }}
            animate={ready ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
          >
            <button onClick={() => go("frames")} data-testid="hero-explore-frames-btn" className="pp-btn rounded-full bg-pp-red px-7 py-3.5 text-white hover:bg-white hover:text-pp-ink" data-cursor="view">
              Explore Frames <ArrowRight size={18} />
            </button>
            <button onClick={() => go("gifts")} data-testid="hero-shop-gifts-btn" className="pp-btn rounded-full border border-white/35 px-7 py-3.5 text-white hover:border-white hover:bg-white/5">
              Shop Photo Gifts <ArrowRight size={18} />
            </button>
            <button onClick={() => go("locations")} data-testid="hero-visit-store-btn" className="overline flex items-center gap-2 text-white/70 transition-colors hover:text-pp-red">
              Visit Store <ArrowRight size={14} />
            </button>
          </motion.div>
        </motion.div>

        {/* Signature hero visual — pointing woman (transparent cutout) */}
        <motion.div
          style={{ y: wallY }}
          className="pointer-events-none absolute bottom-0 right-0 z-[2] hidden h-[92%] w-[44%] lg:block"
          initial={{ opacity: 0, x: 60 }}
          animate={ready ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 1, ease: EASE, delay: 0.4 }}
        >
          <div className="absolute bottom-[6%] right-[8%] h-[60%] w-[60%] rounded-full" style={{ background: "radial-gradient(circle, rgba(255,30,30,0.28) 0%, transparent 70%)" }} />
          <img
            src="/brand/woman.png"
            alt="Smiling PHOTO PIXEL customer pointing towards the slogan"
            className="absolute bottom-0 right-0 h-full w-full object-contain object-bottom"
            style={{ filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.55))" }}
          />
        </motion.div>
      </div>
    </section>
  );
}
